/**
 * useExperimentGeneration
 *
 * Polls GET /api/v1/property/:propertyId/experiment-generation every 5 s
 * while the job is pending or running. Exposes a `retry` action that
 * re-POSTs with force: true to re-enqueue after failure.
 */

import { useEffect, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/lib/contexts/AuthContext"
import {
  logExperimentDegradation,
  logExperimentEvent,
} from "@/lib/observability/experiments"
import {
  getExperimentGeneration,
  triggerExperimentGeneration,
  type ExperimentGenerationJob,
} from "@/lib/api/onboardingTests"

interface UseExperimentGenerationReturn {
  job: ExperimentGenerationJob | null
  isLoading: boolean
  isReady: boolean
  isFailed: boolean
  isRunning: boolean
  /** Fetched successfully but no job exists (404). Distinct from a load error. */
  isMissing: boolean
  isError: boolean
  retry: (pageUrl: string, goal?: string | null) => Promise<void>
}

export function useExperimentGeneration(
  propertyId: string | null
): UseExperimentGenerationReturn {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const query = useQuery<ExperimentGenerationJob | null>({
    queryKey: ["experiment-generation", propertyId],
    queryFn: () =>
      propertyId && token ? getExperimentGeneration(propertyId, token) : null,
    enabled: !!propertyId && !!token,
    // Poll every 5 s while the job is active. A missing job (`data === null`,
    // meaning a fetched 404, as opposed to `undefined` before the first
    // success) also keeps polling: callers re-enqueue on that state, and if we
    // stopped here the caller would sit on "analyzing" forever.
    refetchInterval: (query) => {
      const data = query.state.data
      const status = data?.status
      if (status === "pending" || status === "running") return 5000
      return data === null ? 5000 : false
    },
    staleTime: 0,
  })

  const retry = async (pageUrl: string, goal: string | null = null) => {
    if (!propertyId || !token) return
    await triggerExperimentGeneration(propertyId, pageUrl, goal, token, true)
    queryClient.invalidateQueries({ queryKey: ["experiment-generation", propertyId] })
  }

  const job = query.data ?? null
  const status = job?.status ?? null

  const failureCode = job?.error?.code ?? null

  // Only the moment a running job lands is worth an event. Mounting onto a job
  // that already finished (a revisit, a remount) is not a new outcome, and the
  // property is part of the identity so switching sites isn't read as one.
  const previous = useRef<{ propertyId: string | null; status: string | null }>({
    propertyId: null,
    status: null,
  })

  useEffect(() => {
    const prior = previous.current
    previous.current = { propertyId, status }

    const isTransition =
      prior.propertyId === propertyId && prior.status !== null && prior.status !== status

    if (!propertyId || !isTransition) return

    if (status === "completed") {
      logExperimentEvent("generation_ready", { propertyId })
    } else if (status === "failed") {
      logExperimentDegradation("generation_job_failed", { propertyId }, {
        reason: failureCode ?? "unknown",
      })
    }
  }, [failureCode, propertyId, status])

  return {
    job,
    isLoading: query.isLoading,
    isReady: status === "completed",
    isFailed: status === "failed",
    isRunning: status === "pending" || status === "running",
    isMissing: query.isSuccess && query.data === null,
    isError: query.isError,
    retry,
  }
}
